<template>
  <ul class="flex flex-col gap-xs">
    <CascadingUUIDItem
      v-for="item in filteredItems"
      :key="item.id"
      :item="item"
      :is-expanded="isExpanded"
      :toggle="toggle"
      :checked="handleItemCheck"
      :is-checked="isChecked"
      :editable="editable"
      :sticky-items="stickyItems"
      :show-uneditable="showUneditable"
      :non-selectable-types="nonSelectableTypes"
      class="pr-sm rounded-sm border border-(--border-strong)"
                  :class="{'input-error border-0': error}"

      :tight="tight"
      :links="links"
      :plain="plain"
    />
    <p v-if="error" class="error">{{ error }}</p>
  </ul>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, watch, type PropType } from "vue";
import CascadingUUIDItem from "@/components/misc/cascading-uuid-item.vue";
import type { UUIDLocation } from "@/stores/store-interfaces";

const emit = defineEmits(["update", "appliedAssociation"]);
// Props
const props = defineProps({
  items: {
    type: Array as () => UUIDLocation[],
    required: true,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  associatedItems: {
    type: Array as () => UUIDLocation[],
    required: false,
  },
  showUneditable: {
    type: Boolean,
    default: false,
  },
  tight: {
    type: Boolean,
    default: false,
  },
  expandAll: {
    type: Boolean,
    default: false,
  },
  selectedItems: {
    type: Array as PropType<{ id: string }[]>,
    required: false,
  },
  expandSelected: {
    type: Boolean,
    default: false,
  },
  stickyAssociations: {
    type: Boolean,
    required: false,
    default: false,
  },
  checkRecursively: {
    type: Boolean,
    required: false,
    default: false,
  },
  links: {
    type: Boolean,
    default: false,
  },
  // Read-only display: render labels as plain text (no link underline, no selected-blue).
  plain: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    required: false,
  },
  // Location types whose checkbox is disabled. Checks on these types are ignored so they can
  // never enter the selection (e.g. `region` in the incident footprint picker — ITDEVELOP-2987).
  nonSelectableTypes: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  // Location types that are not offered at all: a node of one of these types and everything under
  // it is left out of the tree (e.g. pods, rack rows, aisles and racks in the contract footprint
  // picker, where the data hall is the smallest footprint — ITDEVELOP-3458).
  hiddenTypes: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
});

// Reactive state for expanded items
const expanded = reactive<Set<string>>(new Set());
const checkedItems = reactive<Set<string>>(new Set());
const usedItems = reactive<Set<string>>(new Set());
const stickyItems = reactive<Set<string>>(new Set());

const filteredItems = computed(() => {
  function filterHierarchy(items: UUIDLocation[]): UUIDLocation[] {
    const result: UUIDLocation[] = [];

    for (const item of items) {
      if (props.hiddenTypes.includes(item.locationType)) continue;
      // Get the item's children
      const children = item.children;

      // Recursively filter children and add them to the current item
      const visibleChildren = filterHierarchy(children);
      const newItem = { ...item, children: visibleChildren };
      result.push(newItem);
      // If the item is hidden but has visible children, pass the children to the parent level
    }
    return result;
  }

  return filterHierarchy(props.items);
});

// Handle toggle functionality
function toggle(e: Event, item: UUIDLocation) {
  e.preventDefault();
  const id = item.id;
  const needsOpening = !expanded.has(id);

  if (needsOpening && item.children.length > 0) {
    expanded.add(id);
  } else {
    expanded.delete(id);
  }
}

function toggleRecursive(item: UUIDLocation, expand: boolean) {
  if (expand) {
    expanded.add(item.id);
  } else {
    expanded.delete(item.id);
  }
  for (const child of item.children) {
    toggleRecursive(child, expand);
  }
}

// Check if an item is expanded
function isExpanded(item: UUIDLocation): boolean {
  return expanded.has(item.id);
}

function handleItemCheck(item: UUIDLocation) {
  // Non-selectable types (e.g. `region`) can never be checked directly — they're grouping
  // nodes the user drills into, not valid selections (ITDEVELOP-2987).
  if (props.nonSelectableTypes.includes(item.locationType)) return;

  const itemId = item.id;

  const shouldCheck = !checkedItems.has(itemId);
  
  if (shouldCheck) {
    // Checking: mark this item as used and make sure parents are checked but not used
    checkedItems.add(itemId);
    usedItems.add(itemId);

    for (const parent of findParents(item)) {
      checkedItems.add(parent.id);
      // parent should not be considered a used (lowest) item if a child was just selected
      usedItems.delete(parent.id);
    }
  } else {
    // Unchecking: remove this item and its descendants from checked/used
    checkRecursive(item, false);

    // After removing, ensure ancestors become 'used' if none of their descendants remain used
    const parents = findParents(item);
    for (const parent of parents) {
      // ensure the parent is marked checked (we want parents to remain visible as checked)
      checkedItems.add(parent.id);
      // if no descendant of this parent is in usedItems, mark the parent as used
      if (!hasUsedDescendant(parent)) {
        usedItems.add(parent.id);
      }
    }
  }

  // If recursive checking is enabled, perform that now (single call)
  if (props.checkRecursively) checkRecursive(item, shouldCheck);
  else {
    if (!isExpanded(item) && item.children.length > 0) {
      expanded.add(item.id);
    }
  }
  if (props.editable) emit('update', checkedItems);
}

function checkRecursive(item: UUIDLocation, check: boolean) {
  if (check) {
    checkedItems.add(item.id);
    usedItems.add(item.id);
  } else {
    checkedItems.delete(item.id);
    usedItems.delete(item.id);
  }

  for (const child of item.children) {
    checkRecursive(child, check);
  }

  toggleRecursive(item, check);
}

function hasUsedDescendant(item: UUIDLocation): boolean {
  for (const child of item.children) {
    if (usedItems.has(child.id) || hasUsedDescendant(child)) return true;
  }
  return false;
}

function findParents(item: UUIDLocation): UUIDLocation[] {
  const parents: UUIDLocation[] = [];

  function traverseHierarchy(current: UUIDLocation | undefined, parent: UUIDLocation | null) {
    if (!current) return;

    // Check if the current item's ID matches the target item's ID
    if (current.id === item.id) {
      if (parent) parents.unshift(parent); // Add parent to the start of the array
      return true; // Found the item
    }

    if (current.children) {
      for (const child of current.children) {
        if (traverseHierarchy(child, current)) {
          if (parent) parents.unshift(parent);
          return true;
        }
      }
    }

    return false; // Item not found in this branch
  }

  // Start traversing from the top-level items
  for (const region of props.items) {
    if (traverseHierarchy(region, null)) break; // Stop searching after finding the item
  }

  return parents;
}

function isChecked(item: UUIDLocation): boolean {
  return checkedItems.has(item.id);
}

// Populate `checkedItems` and propagate to children
function initializeCheckedItems() {
  props.associatedItems?.forEach((item) => {
    if (props.stickyAssociations) {
      stickyItems.add(item.id);
    }
    checkedItems.add(item.id); // Add the item itself
    expanded.add(item.id);
    addChildrenToCheckedItems(item);
  });
}

// Recursive function to add children to `checkedItems`
function addChildrenToCheckedItems(item: UUIDLocation) {
  const children = item.children || [];
  for (const child of children) {
    if (props.stickyAssociations) {
      stickyItems.add(child.id);
    }

    checkedItems.add(child.id);
    expanded.add(child.id);
    addChildrenToCheckedItems(child); // Drill down further
  }
}

function expandAllItems(items: UUIDLocation[] = props.items) {
  for (const item of items) {
    if (item.children.length > 0) {
      expanded.add(item.id);
      expandAllItems(item.children);
    }
  }
}

function expandSelectedItems(items: UUIDLocation[], selectedIds: Set<string>): boolean {
  let containsSelected = false;
  for (const item of items) {
    const selectedChild = expandSelectedItems(item.children, selectedIds);
    if (selectedIds.has(item.id) || selectedChild) {
      containsSelected = true;
      if (item.children.length > 0) expanded.add(item.id);
    }
  }
  return containsSelected;
}

function setup() {
  if (props.items.length === 0) return;

  if (props.expandAll) {
    expandAllItems();
  }

  if (props.selectedItems) {
    const selectedIds = new Set(props.selectedItems.map((item) => item.id));
    props.selectedItems.forEach((item) => {
      checkedItems.add(item.id);
      usedItems.add(item.id);
    });

    if (props.expandSelected) expandSelectedItems(props.items, selectedIds);

    initializeCheckedItems();
  }

  if (props.editable || props.showUneditable) {
    initializeCheckedItems();
  }
}

onMounted(() => {
  setup();
});

watch(
  props.items,
  () => {
    setup();
  },
  { deep: true }
);

watch(
  () => props.associatedItems,
  () => {
    if (props.editable || props.showUneditable) {
      initializeCheckedItems();
      emit("appliedAssociation", checkedItems);
    }
  }
);
</script>
